import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiPlay, FiCheckCircle, FiAlertCircle, FiLoader, FiFilter, 
  FiMessageSquare, FiTrendingUp, FiTarget, FiUserCheck, 
  FiMail, FiCalendar, FiDatabase, FiLayers, FiInfo, FiHelpCircle, FiChevronDown
} from 'react-icons/fi';
import SafeIcon from '../components/SafeIcon';
import { demoFaqs, mockCandidates, mockRoles } from '../data/mockData';

const workflowSteps = [
  { id: 'setup', label: 'Role Setup', desc: 'Define core competencies and AI scoring weights to align your team.', icon: FiTarget },
  { id: 'source', label: 'Talent Sourcing', desc: 'Aggregate candidates from LinkedIn, GitHub, and internal databases instantly.', icon: FiDatabase },
  { id: 'screen', label: 'Radar Screening', desc: 'AI-driven resume ranking identifies the top 5% of your pipeline automatically.', icon: FiLayers },
  { id: 'interview', label: 'Panel Sync', desc: 'Coordinate multi-stage interviews with collaborative, real-time scorecards.', icon: FiCalendar },
  { id: 'offer', label: 'Decision Core', desc: 'Manage approval chains and digital offer letters in a single dashboard.', icon: FiUserCheck },
  { id: 'hire', label: 'Outcome Tracking', desc: 'Analyze hiring velocity and cost-per-hire with automated reporting.', icon: FiTrendingUp },
];

const DemoView = () => {
  const [activeStep, setActiveStep] = useState('setup');
  const [simState, setSimState] = useState('success');
  const [expandedFaq, setExpandedFaq] = useState(null);

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto space-y-16 pb-24 overflow-x-hidden">
      {/* 1. Overview Section */}
      <section className="relative">
        <div className="absolute -top-24 -left-24 w-64 h-64 bg-blue-500/10 rounded-full blur-[100px] pointer-events-none"></div>
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-blue-600/20 text-blue-400 rounded-lg">
            <SafeIcon icon={FiInfo} />
          </div>
          <h2 className="text-sm font-bold uppercase tracking-[0.2em] text-blue-500">Overview / What it does</h2>
        </div>
        <h1 className="text-4xl md:text-5xl font-extrabold text-slate-100 mb-6 leading-tight">
          The Autonomous <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-green-400 font-black">Hiring Radar</span>
        </h1>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <p className="text-lg text-slate-400 leading-relaxed">
              RecruitRay is a B2B SaaS platform designed for high-growth recruiting teams. We solve the problem of **fragmented talent pipelines** and **manual screening fatigue** by unifying the entire hiring lifecycle into a single, AI-enhanced radar dashboard.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-slate-900 border border-slate-800 rounded-xl">
                <p className="text-2xl font-bold text-green-400">40%</p>
                <p className="text-xs text-slate-500 uppercase font-bold tracking-wider">Faster Time-to-Hire</p>
              </div>
              <div className="p-4 bg-slate-900 border border-slate-800 rounded-xl">
                <p className="text-2xl font-bold text-blue-400">92%</p>
                <p className="text-xs text-slate-500 uppercase font-bold tracking-wider">Team Alignment Score</p>
              </div>
            </div>
          </div>
          <div className="hidden lg:block relative">
            <div className="aspect-video bg-slate-900 rounded-2xl border border-slate-800 shadow-2xl relative overflow-hidden group">
               <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-transparent"></div>
               <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center animate-pulse shadow-[0_0_20px_rgba(59,130,246,0.5)]">
                    <SafeIcon icon={FiPlay} className="text-white text-2xl" />
                  </div>
               </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Demo / How It Works Section */}
      <section className="space-y-8">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-green-600/20 text-green-400 rounded-lg">
            <SafeIcon icon={FiPlay} />
          </div>
          <h2 className="text-sm font-bold uppercase tracking-[0.2em] text-green-500">Demo / How It Works</h2>
        </div>

        <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-3xl space-y-8">
          <div className="flex items-center gap-2 mb-2 px-2">
            <SafeIcon icon={FiCheckCircle} className="text-blue-500" />
            <h3 className="text-xl font-bold text-slate-100">Try this flow</h3>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-4 space-y-3">
              {workflowSteps.map((step, idx) => (
                <button
                  key={step.id}
                  onClick={() => setActiveStep(step.id)}
                  className={`w-full text-left p-4 rounded-xl border transition-all flex items-center gap-4 group
                    ${activeStep === step.id 
                      ? 'bg-blue-600/10 border-blue-500/50 text-blue-400 shadow-[0_0_20px_rgba(59,130,246,0.05)]' 
                      : 'bg-slate-950/50 border-slate-800 text-slate-500 hover:border-slate-700'}`}
                >
                  <div className={`p-2 rounded-lg ${activeStep === step.id ? 'bg-blue-500 text-white' : 'bg-slate-800'}`}>
                    <SafeIcon icon={step.icon} />
                  </div>
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-widest opacity-50">Stage {idx + 1}</p>
                    <p className="font-bold text-sm tracking-tight">{step.label}</p>
                  </div>
                </button>
              ))}
            </div>

            <div className="lg:col-span-8 bg-slate-950/80 rounded-2xl p-8 border border-slate-800 flex flex-col justify-center min-h-[350px] relative overflow-hidden">
               <AnimatePresence mode="wait">
                 <motion.div
                   key={activeStep}
                   initial={{ opacity: 0, y: 10 }}
                   animate={{ opacity: 1, y: 0 }}
                   exit={{ opacity: 0, y: -10 }}
                   className="space-y-6 relative z-10"
                 >
                   <div className="inline-block px-4 py-1 rounded-full bg-blue-500/10 text-blue-400 text-xs font-bold border border-blue-500/20">
                     Active Module: {activeStep.toUpperCase()}
                   </div>
                   <h4 className="text-3xl font-bold text-slate-100">
                     {workflowSteps.find(s => s.id === activeStep).label}
                   </h4>
                   <p className="text-slate-400 text-lg leading-relaxed max-w-xl">
                     {workflowSteps.find(s => s.id === activeStep).desc} This workflow ensures that zero high-quality candidates slip through the cracks while reducing administrative burden on your hiring managers.
                   </p>
                   <div className="flex gap-4">
                      <button className="bg-blue-600 hover:bg-blue-500 text-white px-6 py-2.5 rounded-lg font-bold text-sm transition-all shadow-[0_0_15px_rgba(59,130,246,0.3)]">
                        Initialize Simulation
                      </button>
                   </div>
                 </motion.div>
               </AnimatePresence>
               <div className="absolute -bottom-12 -right-12 opacity-5">
                  <SafeIcon icon={workflowSteps.find(s => s.id === activeStep).icon} className="text-[15rem]" />
               </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3 & 4. Sample Data & Simulated States */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <section className="space-y-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-slate-800 text-slate-400 rounded-lg">
              <SafeIcon icon={FiDatabase} />
            </div>
            <h2 className="text-sm font-bold uppercase tracking-[0.2em] text-slate-400">Sample Data / Real Records</h2>
          </div>
          
          <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-950 border-b border-slate-800">
                <tr>
                  <th className="px-4 py-3 font-bold text-slate-500 uppercase text-[10px]">Candidate</th>
                  <th className="px-4 py-3 font-bold text-slate-500 uppercase text-[10px]">Fit Score</th>
                  <th className="px-4 py-3 font-bold text-slate-500 uppercase text-[10px]">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {mockCandidates.slice(0, 5).map(c => (
                  <tr key={c.id} className="hover:bg-slate-800/30 transition-colors">
                    <td className="px-4 py-4">
                      <p className="font-bold text-slate-200">{c.name}</p>
                      <p className="text-[10px] text-slate-500">{c.email}</p>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <div className="w-12 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                          <div className="h-full bg-blue-500" style={{ width: `${c.score}%` }}></div>
                        </div>
                        <span className="font-mono text-xs">{c.score}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold bg-slate-800 ${c.status.includes('Track') ? 'text-green-400' : 'text-amber-400'}`}>
                        {c.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="space-y-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-600/20 text-amber-400 rounded-lg">
              <SafeIcon icon={FiLoader} />
            </div>
            <h2 className="text-sm font-bold uppercase tracking-[0.2em] text-amber-500">Simulated States</h2>
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-2xl flex flex-col h-full min-h-[300px]">
            <div className="p-3 bg-slate-950 border-b border-slate-800 flex flex-wrap gap-2">
              {['success', 'loading', 'empty', 'error'].map(state => (
                <button 
                  key={state}
                  onClick={() => setSimState(state)}
                  className={`px-3 py-1 rounded-md text-[10px] font-black uppercase tracking-widest transition-all ${simState === state ? 'bg-blue-600 text-white shadow-[0_0_10px_rgba(59,130,246,0.3)]' : 'bg-slate-800 text-slate-500 hover:text-slate-300'}`}
                >
                  {state}
                </button>
              ))}
            </div>
            <div className="flex-1 flex items-center justify-center p-8">
              <AnimatePresence mode="wait">
                {simState === 'success' && (
                  <motion.div key="s" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center space-y-3">
                    <div className="w-16 h-16 bg-green-500/10 text-green-500 rounded-full flex items-center justify-center mx-auto border border-green-500/20 shadow-[0_0_20px_rgba(34,197,94,0.1)]">
                      <SafeIcon icon={FiCheckCircle} className="text-3xl" />
                    </div>
                    <h5 className="font-bold text-slate-200">System Nominal</h5>
                    <p className="text-xs text-slate-500">Real-time radar syncing with 4 active data sources.</p>
                  </motion.div>
                )}
                {simState === 'loading' && (
                  <motion.div key="l" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center space-y-3">
                    <div className="w-12 h-12 border-3 border-blue-500/20 border-t-blue-500 rounded-full animate-spin mx-auto"></div>
                    <p className="text-xs text-slate-500 font-bold uppercase tracking-widest animate-pulse">Processing Pipeline...</p>
                  </motion.div>
                )}
                {simState === 'empty' && (
                  <motion.div key="e" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center space-y-3 opacity-40">
                    <SafeIcon icon={FiFilter} className="text-4xl mx-auto text-slate-600" />
                    <h5 className="font-bold text-slate-400">No Records Found</h5>
                    <p className="text-xs text-slate-600">Adjust radar filters to see more profiles.</p>
                  </motion.div>
                )}
                {simState === 'error' && (
                  <motion.div key="er" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center space-y-3">
                    <div className="w-16 h-16 bg-rose-500/10 text-rose-500 rounded-full flex items-center justify-center mx-auto border border-rose-500/20">
                      <SafeIcon icon={FiAlertCircle} className="text-3xl" />
                    </div>
                    <h5 className="font-bold text-slate-200">Sync Interrupted</h5>
                    <p className="text-xs text-slate-500">LinkedIn API re-authentication required.</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </section>
      </div>

      {/* 5. FAQ Section */}
      <section className="space-y-8">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-600/20 text-indigo-400 rounded-lg">
            <SafeIcon icon={FiHelpCircle} />
          </div>
          <h2 className="text-sm font-bold uppercase tracking-[0.2em] text-indigo-500">Common Questions / FAQ</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {demoFaqs.map((faq, idx) => (
            <div 
              key={idx} 
              className={`p-5 rounded-2xl border transition-all cursor-pointer ${expandedFaq === idx ? 'bg-slate-900 border-indigo-500/30' : 'bg-slate-950 border-slate-800 hover:border-slate-700'}`}
              onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
            >
              <div className="flex justify-between items-center gap-4">
                <h4 className="font-bold text-slate-200 text-sm leading-tight">{faq.q}</h4>
                <SafeIcon icon={FiChevronDown} className={`text-slate-500 transition-transform ${expandedFaq === idx ? 'rotate-180' : ''}`} />
              </div>
              <AnimatePresence>
                {expandedFaq === idx && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <p className="text-xs text-slate-400 mt-4 leading-relaxed border-t border-slate-800 pt-4">
                      {faq.a}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </section>

      <footer className="text-center pt-20 border-t border-slate-900">
        <p className="text-[10px] uppercase tracking-[0.5em] font-black text-slate-700">
          Made by LingLink AI and DigLab AI
        </p>
      </footer>
    </div>
  );
};

export default DemoView;